
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { normalizeCity, normalizeSuburb, normalizeProvinceState, normalizePostalCode, normalizeString } from '@/lib/addressUtils';

interface AddressFieldsProps {
  form: UseFormReturn<any>;
  prefix?: string;
  required?: boolean;
}

export function AddressFields({ form, prefix = '', required = false }: AddressFieldsProps) {
  const getFieldName = (field: string) => prefix ? `${prefix}_${field}` : field;

  // Normalization handler to clean up address data on blur
  const handleNormalization = (fieldName: string, normalizer: (value: string) => string) => (e: React.FocusEvent<HTMLInputElement>) => {
    const normalizedValue = normalizer(e.target.value);
    if (normalizedValue !== e.target.value) {
      form.setValue(fieldName as any, normalizedValue);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        <FormField
          control={form.control}
          name={getFieldName('street_address')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Street Address{required && ' *'}</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  placeholder="123 Main Street" 
                  onBlur={(e) => {
                    field.onBlur();
                    handleNormalization(getFieldName('street_address'), normalizeString)(e);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name={getFieldName('suburb')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Suburb</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  placeholder="Suburb name" 
                  onBlur={(e) => {
                    field.onBlur();
                    handleNormalization(getFieldName('suburb'), normalizeSuburb)(e);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name={getFieldName('city')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>City{required && ' *'}</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  placeholder="City name" 
                  onBlur={(e) => {
                    field.onBlur();
                    handleNormalization(getFieldName('city'), normalizeCity)(e);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name={getFieldName('province_state')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Province/State</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  placeholder="Province or State" 
                  onBlur={(e) => {
                    field.onBlur();
                    handleNormalization(getFieldName('province_state'), normalizeProvinceState)(e);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name={getFieldName('postal_code')}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Postal Code</FormLabel>
            <FormControl>
              <Input 
                {...field} 
                placeholder="12345" 
                onBlur={(e) => {
                  field.onBlur();
                  handleNormalization(getFieldName('postal_code'), normalizePostalCode)(e);
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
